/**
 * ESG-3960: the Insights drill-down "break down by <level>" resolution, in one place.
 *
 * All four drill-downs (Scope 1, Scope 2, Scope 3 and the Scope 3 category detail) had their own copy
 * of `resolveLevels`, three as near-identical helpers and one inlined in a `useMemo`. The level selector
 * QA added then went onto two of the four, so the pages disagreed about whether a level could be chosen
 * at all. Pure functions live here so they can be unit-tested; the React state lives in
 * `hooks/useBreakdownLevel.js`, which is a thin wrapper over these.
 */

/**
 * Page size for a by-dimension table request. The backend clamps to the same ceiling, so asking for more
 * gains nothing: on the dimension-filtered path it recomputes one full report per item returned.
 */
export const BY_DIMENSION_PAGE_SIZE = 200;

/**
 * ESG-3960: the quarterly view fans out FIVE requests (Q1..Q4 + the year), each recomputing per item, so
 * its cost is five times the annual view's. A smaller page keeps that in proportion — and a quarterly
 * table with 200 rows × 5 columns is unreadable well before the limit bites.
 */
export const QUARTERLY_BY_DIMENSION_PAGE_SIZE = 25;

export const DIMENSION_LEVEL_KEYS = [
  "dimensionOne",
  "dimensionTwo",
  "dimensionThree",
  "dimensionFour",
  "dimensionFive",
];

/** The level keys the user has an active filter selection at, in level order. */
export const selectedLevelKeys = (dimension) =>
  DIMENSION_LEVEL_KEYS.filter((k) => (dimension?.[k]?.length || 0) > 0);

/**
 * The level currently filtered to (`current`) and the level below it (`next`) — the level a table breaks
 * down into when the user has not chosen one explicitly.
 *
 * `next` is undefined once the filter reaches the lowest configured level. That is NOT a reason to hide
 * the table: the user can still pick a level from the dropdown, including one at or above the filter.
 * Only an org with no configured dimensions at all has nothing to show.
 */
export const resolveLevels = (dimension, configuredKeys) => {
  if (!configuredKeys || configuredKeys.length === 0) return { current: null, next: null };
  const selected = selectedLevelKeys(dimension);
  const currentKey = selected.length > 0 ? selected[selected.length - 1] : null;
  const currentIdx = currentKey ? configuredKeys.indexOf(currentKey) : -1;
  return { current: currentKey, next: configuredKeys[currentIdx + 1] };
};

/** The org's dimension levels that are actually configured (have both an id and a name), in order. */
export const configuredLevelKeysOf = (organizationStructure) =>
  DIMENSION_LEVEL_KEYS.filter(
    (k) => organizationStructure?.dimensions?.[k]?.id && organizationStructure?.dimensions?.[k]?.name
  );

/**
 * The `dimensionOne..Five=1,2` query params for the current filter. Levels with no selection are
 * omitted entirely rather than sent empty — the backend treats a present-but-empty list as "no selection
 * at this level" (ESG-3958), but omitting is unambiguous.
 */
export const dimensionFilterParams = (dimension) =>
  Object.fromEntries(
    selectedLevelKeys(dimension).map((k) => [k, dimension[k].join(",")])
  );
